import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'horizontal';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const category = categories.find((c) => c.id === article.categoryId);
  const dateStr = new Date(article.publishedAt).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block relative rounded-lg overflow-hidden cursor-pointer h-[420px] md:h-[500px]"
      >
        <img
          src={article.imageUrl}
          alt={article.title}
          title={article.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="flex items-center gap-2 mb-3">
            {article.isHot && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500/90 text-background-50 whitespace-nowrap">
                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-fire-line"></i></span>
                热门
              </span>
            )}
            {category && (
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-background-50/20 text-background-50 whitespace-nowrap">
                {category.name}
              </span>
            )}
          </div>
          <h2 className="text-xl md:text-3xl font-semibold text-background-50 mb-2 line-clamp-2 leading-tight">
            {article.title}
          </h2>
          <p className="text-background-200 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-background-300 text-xs">
            <span>{dateStr}</span>
            <span>·</span>
            <span>{(article.viewCount / 10000).toFixed(1)}万阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex flex-col sm:flex-row gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            {article.isHot && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-fire-line"></i></span>
                热门
              </span>
            )}
            {category && (
              <span className="text-xs text-primary-600 font-medium whitespace-nowrap">{category.name}</span>
            )}
          </div>
          <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 mb-1 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-500 line-clamp-2 mb-2">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span>{dateStr}</span>
            <span>·</span>
            <span>{(article.viewCount / 10000).toFixed(1)}万阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
    >
      <div className="h-48 overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {article.isHot && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-fire-line"></i></span>
              热门
            </span>
          )}
          {category && (
            <span className="text-xs text-primary-600 font-medium whitespace-nowrap">{category.name}</span>
          )}
        </div>
        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors leading-snug">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span>{dateStr}</span>
          <span>{(article.viewCount / 10000).toFixed(1)}万阅读</span>
        </div>
      </div>
    </Link>
  );
}